import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import CloseIcon from '@mui/icons-material/Close';
import { CompanySearchV2 } from 'services/openapiClient';
import KosmosCompanyResultWithSmartGovernance from 'kosmos/components/KosmosCompanyResultWithSmartGovernance';
import { useTranslation } from 'react-i18next';
import { countryOptions } from 'utils/countries';
import { CompanySearchDto } from 'models/dtoTypes';

const PREFIX = 'CompanySearchDialog';

const classes = {
  root: `${PREFIX}-root`,
  box: `${PREFIX}-box`,
  loadingBox: `${PREFIX}-loadingBox`,
  resultContainer: `${PREFIX}-resultContainer`,
};

const Root = styled('div')(() => ({
  [`&.${classes.root}`]: {
    position: 'relative',
    width: '100%',
  },
  [`& .${classes.box}`]: {
    paddingTop: 16,
    display: 'flex',
    alignItems: 'center',
    minWidth: 800,
    '& > *': {
      marginRight: 15,
    },
  },
  [`& .${classes.loadingBox}`]: {
    width: '100%',
  },
  [`& .${classes.resultContainer}`]: {
    marginTop: 20,
    marginBottom: 20,
  },
}));

const EMPTY_COUNTRY = {
  label: 'Select Country',
  alpha3: '',
};

export interface IMessage {
  message?: string;
  severity?: 'error' | 'info';
}

export interface ICompanySearchResult {
  loading: boolean;
  CompaniesWithPageMeta?: any;
  messages: IMessage[];
}

interface ICountryOption {
  label: string;
  alpha3: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSelectCompany: (company: CompanySearchDto) => void;
  initialCompanyName?: string;
  initialCompanyNumber?: string;
  initialAddressLine?: string;
  initialPostalCode?: string;
  initialCountry?: string;
}

export default function CompanyGenericSearchDialog({
  open,
  onClose,
  onSelectCompany,
  initialCompanyName,
  initialCompanyNumber,
  initialAddressLine,
  initialPostalCode,
  initialCountry,
}: Props): JSX.Element {
  const { t } = useTranslation('default');
  const [companyName, setCompanyName] = useState(initialCompanyName || '');
  const [companyNumber, setCompanyNumber] = useState(initialCompanyNumber || '');
  const [addressLine, setAddressLine] = useState(initialAddressLine || '');
  const [postalCode, setPostalCode] = useState(initialPostalCode || '');
  const [countries, setCountries] = useState<ICountryOption[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<ICountryOption>(EMPTY_COUNTRY);
  const [selectedCompany, setSelectedCompany] = useState<CompanySearchDto>();
  const [result, setResult] = useState<ICompanySearchResult>({
    loading: false,
    CompaniesWithPageMeta: undefined,
    messages: [],
  });
  const [dataLoading, setDataLoading] = useState<boolean>(false);

  const fetchCountries = () => {
    const options = countryOptions();
    let formattedOptions = options.map((item) => ({
      label: item.label || '',
      alpha3: item.alias || '',
    }));
    formattedOptions = formattedOptions.filter((a) => a.alpha3 !== 'world');
    formattedOptions.unshift(EMPTY_COUNTRY);
    setCountries(formattedOptions);
    if (initialCountry) {
      let country: ICountryOption = EMPTY_COUNTRY;
      options.forEach((item) => {
        if (item.alias.toLowerCase() === initialCountry.toLowerCase()) {
          country = {
            label: item.label || '',
            alpha3: item.alias || '',
          };
        }
      });
      setSelectedCountry(country);
      return country.alpha3;
    }
  };

  useEffect(() => {
    const countryCode = fetchCountries();
    if ((companyName || companyNumber) && countryCode) {
      fetchCompanies(countryCode);
    }
  }, []);

  const validateInputFields = (countryCode: string) => {
    if (companyNumber === '' && (companyName === '' || companyName.length < 3)) {
      return false;
    }
    if (countryCode === '') {
      return false;
    }
    return true;
  };

  const fetchCompanies = async (selectedCountryCode = '') => {
    const countryCode = selectedCountryCode !== '' ? selectedCountryCode : selectedCountry.alpha3;
    const isValid = validateInputFields(countryCode);
    if (!isValid) {
      setResult({
        loading: false,
        messages: [
          {
            message: t('Need Company Name/Company Number and Company Country'),
            severity: 'info',
          },
        ],
      });
      return;
    }
    setDataLoading(true);
    setResult({ loading: true, messages: [] });
    try {
      const companiesWithPageMeta = await CompanySearchV2({
        companyName,
        companyNumber,
        countryCode,
        addressLine,
        postalCode,
      });
      const hasCompany = companiesWithPageMeta?.items && companiesWithPageMeta.items.length > 0;
      const messages: IMessage[] = [];
      if (!hasCompany) {
        messages.push({ message: 'No record found', severity: 'info' });
      }
      setResult({
        loading: false,
        CompaniesWithPageMeta: companiesWithPageMeta,
        messages: messages,
      });
      setDataLoading(false);
    } catch (e) {
      setResult({
        loading: false,
        CompaniesWithPageMeta: undefined,
        messages: [
          {
            message: e instanceof Error ? e.message : String(e),
            severity: 'error',
          },
        ],
      });
      setDataLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchCompanies();
  };

  const handleReset = () => {
    setCompanyName('');
    setCompanyNumber('');
    setAddressLine('');
    setPostalCode('');
    setSelectedCountry(EMPTY_COUNTRY);
    setResult({
      loading: false,
      messages: [
        {
          message: t('Need Company Name/Company Number and Company Country'),
          severity: 'info',
        },
      ],
    });
  };

  const handleCompanySelect = (id?: string) => {
    const { CompaniesWithPageMeta = {} } = result;
    const { items } = CompaniesWithPageMeta;
    if (!id || id === '') {
      return null;
    }
    const comp = items.find((a: CompanySearchDto) => a.id === id);
    setSelectedCompany(comp);
  };

  const handleCompanySelectionReturn = () => {
    if (selectedCompany) {
      onSelectCompany(selectedCompany);
      handleReset();
    } else {
      return;
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const renderResult = () => {
    const { CompaniesWithPageMeta = {}, loading } = result;
    const { items } = CompaniesWithPageMeta;
    if (loading) {
      return (
        <Box className={classes.loadingBox}>
          <Skeleton height={100} />
          <Skeleton height={100} />
          <Skeleton height={100} />
        </Box>
      );
    } else if (items && items.length > 0) {
      return (
        <KosmosCompanyResultWithSmartGovernance
          companies={items}
          onSelectCompany={(id?: string) => handleCompanySelect(id)}
          multiSearch={true}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Company Search</DialogTitle>
      <DialogContent>
        <Root className={classes.root}>
          <Box component="form" onSubmit={handleSearch}>
            <Box className={classes.box}>
              <TextField
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder={t('Company Name')}
                sx={{ width: 200 }}
                size="small"
              />
              <TextField
                value={companyNumber}
                onChange={(e) => setCompanyNumber(e.target.value)}
                placeholder={t('Company Number')}
                sx={{ width: 200 }}
                size="small"
              />
              <Autocomplete
                value={selectedCountry}
                onChange={(_, newValue) => setSelectedCountry(newValue || EMPTY_COUNTRY)}
                options={countries}
                sx={{ width: 200 }}
                renderInput={(params: any) => (
                  <TextField {...params} label={t('Country')} size="small" />
                )}
              />
              <TextField
                value={addressLine}
                onChange={(e) => setAddressLine(e.target.value)}
                placeholder={t('Company Address')}
                sx={{ width: 200 }}
                size="small"
              />
              <TextField
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder={t('Postal Code')}
                sx={{ width: 200 }}
                size="small"
              />
              <Button
                onClick={handleSearch}
                variant="contained"
                color="primary"
                endIcon={<SearchIcon />}
                disabled={dataLoading}
              >
                {t('Search')}
              </Button>
              <Button
                onClick={handleReset}
                variant="contained"
                color="secondary"
                endIcon={<CloseIcon />}
              >
                {t('Reset')}
              </Button>
            </Box>
          </Box>
          <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
            {result.messages.map((m, i) => (
              <Alert key={i} severity={m.severity} style={{ marginTop: '16px' }}>
                {m.message}
              </Alert>
            ))}
          </Stack>
          {renderResult()}
        </Root>
      </DialogContent>
      <Box boxShadow={2}>
        <DialogActions>
          <Button
            disabled={result.loading}
            variant="outlined"
            onClick={handleClose}
            color="primary"
          >
            {t('cancel')}
          </Button>
          <Button
            disabled={result.loading}
            variant="contained"
            color="primary"
            autoFocus
            onClick={() => handleCompanySelectionReturn()}
          >
            {t('done')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
